import {
  sItemIntToKey,
  sItemKeyToInt,
  StockItemUpdateFieldValue,
  StockItemUpdateObj,
} from '../../../shared-utilities/models-old/datastructures';

const TAG_FIELD_KEY: string = sItemKeyToInt._tags;

export interface IApiEventTagChange {
  n?: string[];
  o?: string[];
}

export type IApiEventTaggedItems = Record<string, IApiEventTagChange | string[]>;

/**
 * Collect int/string field keys present on either side of an api event stock change.
 */
export function collectStockUpdateFieldKeys(change: StockItemUpdateObj): Set<string> {
  return new Set<string>([
    ...Object.keys(change.n ?? {}),
    ...Object.keys(change.o ?? {}),
  ]);
}

function normalizeTagList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(String);
}

function tagsAreEqual(a: unknown, b: unknown): boolean {
  const left: string[] = normalizeTagList(a).sort();
  const right: string[] = normalizeTagList(b).sort();
  if (left.length !== right.length) {
    return false;
  }
  return left.every((tag: string, index: number) => tag === right[index]);
}

/**
 * Merge backend-managed tag changes from the api event document into stock view changes.
 */
export function mergeTagChangesIntoStockViewChanges(
  stockViewChanges: Record<string, StockItemUpdateObj>,
  taggedItems?: IApiEventTaggedItems,
): Record<string, StockItemUpdateObj> {
  const merged: Record<string, StockItemUpdateObj> = Object.entries(stockViewChanges ?? {}).reduce(
    (acc: Record<string, StockItemUpdateObj>, [code, change]: [string, StockItemUpdateObj]) => {
      acc[code] = {
        ...change,
        n: {...(change.n ?? {})},
        o: {...(change.o ?? {})},
      };
      return acc;
    },
    {},
  );

  if (!taggedItems) {
    return merged;
  }

  Object.entries(taggedItems).forEach(([code, tagChange]: [string, IApiEventTagChange | string[]]) => {
    const existing: StockItemUpdateObj = merged[code] ?? {n: {}, o: {}, details: {}};
    let oldTags: string[] | undefined;
    let newTags: string[] | undefined;

    if (Array.isArray(tagChange)) {
      newTags = tagChange;
      oldTags = existing.o?.[TAG_FIELD_KEY] as string[] | undefined;
    } else {
      oldTags = tagChange.o;
      newTags = tagChange.n;
    }

    const normalizedOld: string[] = normalizeTagList(oldTags);
    const normalizedNew: string[] = normalizeTagList(newTags);

    if (tagsAreEqual(normalizedOld, normalizedNew)) {
      return;
    }

    merged[code] = {
      ...existing,
      n: {...existing.n, [TAG_FIELD_KEY]: normalizedNew},
      o: {...existing.o, [TAG_FIELD_KEY]: normalizedOld},
    };
  });

  return merged;
}

/**
 * Remap int-keyed api event fields to string keys and swap n/o for revert submissions.
 */
export function buildStockUpdateRevertPayload(
  changes: Record<string, StockItemUpdateObj>,
  selectedCodes: string[],
): Record<string, StockItemUpdateObj> {
  const payload: Record<string, StockItemUpdateObj> = {};

  selectedCodes.forEach((code: string) => {
    const change: StockItemUpdateObj | undefined = changes[code];
    if (!change) {
      return;
    }

    const fieldKeys: Set<string> = collectStockUpdateFieldKeys(change);
    const remappedN: Record<string, StockItemUpdateFieldValue> = {};
    const remappedO: Record<string, StockItemUpdateFieldValue> = {};

    fieldKeys.forEach((key: string) => {
      const mappedKey: string = sItemIntToKey[key] ?? key;
      if (change.o && Object.prototype.hasOwnProperty.call(change.o, key)) {
        remappedN[mappedKey] = change.o[key];
      }
      if (change.n && Object.prototype.hasOwnProperty.call(change.n, key)) {
        remappedO[mappedKey] = change.n[key];
      }
    });

    if (Object.keys(remappedN).length === 0) {
      return;
    }

    payload[code] = {
      details: change.details ?? {[1]: ''},
      n: remappedN,
      o: remappedO,
    };
  });

  return payload;
}

/**
 * Extract target tag arrays from a revert payload for direct tag restoration.
 */
export function extractStockTagRestorePayload(
  stockViewChanges: Record<string, StockItemUpdateObj>,
): Record<string, string[]> {
  const tagRestores: Record<string, string[]> = {};

  Object.entries(stockViewChanges).forEach(([code, change]: [string, StockItemUpdateObj]) => {
    const tags: StockItemUpdateFieldValue | undefined = change.n?._tags ?? change.n?.[TAG_FIELD_KEY];
    if (Array.isArray(tags)) {
      tagRestores[code] = tags.map(String);
    }
  });

  return tagRestores;
}
